import { useParams, Navigate, Link } from "react-router-dom";
import { MapPin, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { QuickAnswer } from "@/components/site/QuickAnswer";
import { getMetro, primaryMetros } from "@/data/site/serviceAreas";
import { brand } from "@/data/site/brand";
import { serviceCategories } from "@/data/site/services";

const ServiceAreaDetailPage = () => {
  const { slug = "" } = useParams();
  const metro = getMetro(slug);
  if (!metro || !metro.content) return <Navigate to="/service-areas" replace />;

  const path = `/service-areas/${metro.slug}`;
  const title = `IRS Tax Resolution in ${metro.city}, ${metro.state}`;
  const description = `CPA-led IRS tax resolution and representation for ${metro.city}, ${metro.state} taxpayers and businesses — Offers in Compromise, levy releases, audits, and back-tax help.`;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    name: `${brand.legalName} — Serving ${metro.city}, ${metro.state}`,
    description,
    url: `${brand.siteUrl}${path}`,
    telephone: brand.phoneTel,
    areaServed: {
      "@type": "City",
      name: metro.city,
      containedInPlace: { "@type": "State", name: metro.state },
    },
    provider: {
      "@type": "Organization",
      name: brand.legalName,
      url: brand.siteUrl,
      telephone: brand.phoneTel,
      address: {
        "@type": "PostalAddress",
        streetAddress: brand.address.street,
        addressLocality: brand.address.city,
        addressRegion: brand.address.region,
        postalCode: brand.address.postalCode,
        addressCountry: brand.address.country,
      },
    },
    ...(metro.latitude && metro.longitude
      ? { geo: { "@type": "GeoCoordinates", latitude: metro.latitude, longitude: metro.longitude } }
      : {}),
  };

  // Pull the top service across all categories for quick cross-linking.
  const topServices = serviceCategories.flatMap((c) =>
    c.services.slice(0, 1).map((s) => ({
      title: s.title,
      href: `/services/${c.slug}/${s.slug}`,
    })),
  );

  return (
    <SiteLayout>
      <PageSEO
        title={title}
        description={description}
        path={path}
        image={stockImages.irsBuilding.src}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Service Areas", href: "/service-areas" },
          { name: `${metro.city}, ${metro.state}`, href: path },
        ]}
        jsonLd={localBusinessSchema}
      />

      <section className="relative bg-gradient-navy text-white overflow-hidden">
        <HeroBackground image={stockImages.irsBuilding} priority />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Service Areas", href: "/service-areas" },
              { name: `${metro.city}, ${metro.state}` },
            ]} tone="dark"
          />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mt-2 mb-4">
            Serving {metro.city}, {metro.state}
          </p>
          <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
            IRS resolution for{" "}
            <span className="text-gold">{metro.city}</span> taxpayers and businesses.
          </h1>
          <p className="text-lg text-white/80 max-w-2xl leading-relaxed">{metro.content.intro}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-6 py-3.5 rounded"
            >
              <Phone className="w-4 h-4" /> Call {brand.phone}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3.5 rounded border border-white/20"
            >
              Free case review <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-32 bg-white">
        <div className="container mx-auto px-4 max-w-3xl space-y-14">
          <QuickAnswer
            heading={`Do we represent ${metro.city} taxpayers?`}
            answer={`Yes. CPAs are federally authorized to represent taxpayers before the IRS in all 50 states. We work with ${metro.city}, ${metro.state} clients through secure document exchange, phone, and video — most cases are handled without a single in-person meeting.`}
            bullets={[
              `Active resolution cases throughout the ${metro.city} metro.`,
              `Coordinated handling of any state or local tax issues alongside federal exposure.`,
              `Flat-fee engagements with no sales pressure.`,
            ]}
          />

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-3">
              Local economic context
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-4 leading-tight">
              Why {metro.city} cases look the way they do.
            </h2>
            <p className="text-foreground/85 leading-[1.75] text-[17px]">{metro.content.economy}</p>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-3">
              IRS context
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-4 leading-tight">
              How {metro.city} cases route through the IRS.
            </h2>
            <p className="text-foreground/85 leading-[1.75] text-[17px]">
              {metro.content.irsContext}
            </p>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-3">
              Neighborhoods we serve
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {metro.content.neighborhoods.map((n) => (
                <li key={n} className="flex items-start gap-2 text-foreground/85">
                  <CheckCircle2 className="w-4 h-4 text-gold mt-1 flex-shrink-0" /> {n}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-3">
              Services available in {metro.city}
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-6 leading-tight">
              Every IRS resolution tool, locally represented.
            </h2>
            <div className="grid sm:grid-cols-2 gap-px bg-border">
              {topServices.map((s) => (
                <Link
                  key={s.href}
                  to={s.href}
                  className="group bg-white p-5 hover:bg-secondary/30 transition-colors flex items-center justify-between gap-3"
                >
                  <span className="font-heading font-semibold text-navy group-hover:text-gold transition-colors">
                    {s.title}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-10 flex flex-col sm:flex-row sm:items-center gap-4">
            <MapPin className="w-5 h-5 text-gold shrink-0" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              Headquartered at {brand.address.full} — we meet by appointment in our Prosper office and
              represent {metro.city}, {metro.state} clients remotely the rest of the time.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary/40 border-y border-border">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-3">
            Other Texas metros we serve
          </p>
          <ul className="grid sm:grid-cols-3 md:grid-cols-4 gap-px bg-border">
            {primaryMetros
              .filter((m) => m.slug !== metro.slug)
              .map((m) => (
                <li key={m.slug} className="bg-white">
                  <Link
                    to={`/service-areas/${m.slug}`}
                    className="block p-4 text-sm font-semibold text-navy hover:text-gold"
                  >
                    {m.city}, {m.state}
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default ServiceAreaDetailPage;
